import { useRef } from 'react';

import { useSession } from '@/providers/SessionProvider/SessionProvider';
import useGetClientId from '@/providers/UserPermissionsProvider/hooks/useGetClientId';
import { UserRole, getUserRole, hasRequiredRole } from '@/utils/roleUtils';

const useRoleAccess = () => {
  const { isCurrentClientLoading, isOrgUsersLoading, accountLevelRoles } =
    useSession();
  const clientId = useGetClientId();

  const isLoading = isCurrentClientLoading || isOrgUsersLoading;

  // Track if we've ever finished loading for the current client
  const hasLoadedOnce = useRef(false);
  const previousClientId = useRef(clientId);

  // Reset hasLoadedOnce when client changes (account switching)
  // Use else-if to ensure these are mutually exclusive
  if (previousClientId.current !== clientId) {
    hasLoadedOnce.current = false;
    previousClientId.current = clientId;
  } else if (!isLoading) {
    hasLoadedOnce.current = true;
  }

  // Don't compute userRole if we don't have the data yet (initial load)
  const userRole = !accountLevelRoles ? null : getUserRole(accountLevelRoles);

  return {
    userRole,
    isLoading,
    isInitialLoad: isLoading && !hasLoadedOnce.current,
    hasRole: (role: UserRole) => hasRequiredRole(userRole, role),
  };
};

export default useRoleAccess;
